import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth';
import type { AuthenticatedUser } from '@hygge/shared';
import { USER_ROLES, grantsUnscoped } from '@hygge/shared';

// Whitelisted scope suffixes that let a scoped user permission satisfy an
// un-scoped base requirement (e.g. `recruitment.candidates.view.assigned`
// satisfies a `recruitment.candidates.view` check). Mirrors the backend
// `PermissionsGuard.SCOPE_SUFFIXES` so the FE and BE agree on what a holder of
// a scoped grant can see — the actual row-level narrowing still happens server
// side. Limited to a whitelist so unrelated `view.<x>` codes don't widen.
const SCOPE_SUFFIXES = ['all', 'assigned', 'self', 'own', 'team'];

/** See and own every board: the one company-wide code that reaches inside one. */
const BOARDS_ADMIN = 'boards.admin';

export function matchPermission(userPerm: string, required: string): boolean {
  if (grantsUnscoped(userPerm, required)) return true;
  // Scope hierarchy: a known scoped variant satisfies the base permission.
  if (userPerm.startsWith(required + '.')) {
    const tail = userPerm.slice(required.length + 1);
    if (SCOPE_SUFFIXES.includes(tail)) return true;
  }
  return false;
}

export function hasPermission(user: AuthenticatedUser | null, code: string): boolean {
  if (!user) return false;
  // Mirror the backend bypass: super_admin always passes permission checks
  // regardless of what's enumerated in role_permissions — EXCEPT for opt-in
  // feature-flag style codes (like the placeholder toggle) which super_admin
  // can choose to enable/disable for themselves.
  if (user.role === USER_ROLES.SUPER_ADMIN && code !== PLACEHOLDER_PERMISSION) return true;
  if (!user.permissions?.length) return false;
  // Mirror `PermissionsGuard`: `boards.admin` is the grantable form of seeing
  // and owning every board, so it satisfies any board requirement on its own.
  // Without this the code opens every board API call and no screen — the nav
  // item and the Boards route both ask for `boards.view`, which it is not.
  if (
    code.startsWith('boards.') &&
    user.permissions.some((p) => matchPermission(p, BOARDS_ADMIN))
  ) {
    return true;
  }
  return user.permissions.some((p) => matchPermission(p, code));
}

/** Permission code for the "Coming Soon" placeholder screen */
export const PLACEHOLDER_PERMISSION = 'settings.general.showPlaceholder';

export function hasPlaceholderPermission(user: AuthenticatedUser | null): boolean {
  return hasPermission(user, PLACEHOLDER_PERMISSION);
}

export function hasPermissions(
  user: AuthenticatedUser | null,
  codes: string[],
  mode: 'all' | 'any' = 'all'
): boolean {
  if (!user) return false;
  // Route via hasPermission so the super_admin bypass is consistent and the
  // PLACEHOLDER_PERMISSION exception applies uniformly.
  const check = (code: string) => hasPermission(user, code);
  return mode === 'all' ? codes.every(check) : codes.some(check);
}

export function isSuperAdmin(user: AuthenticatedUser | null): boolean {
  return user?.role === USER_ROLES.SUPER_ADMIN;
}

// --------------- Hooks ---------------

export function usePermission(code: string): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => hasPermission(user, code), [user, code]);
}

export function usePermissions(codes: string[], mode: 'all' | 'any' = 'all'): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => hasPermissions(user, codes, mode), [user, codes, mode]);
}

export function useIsSuperAdmin(): boolean {
  const user = useAuthStore((s) => s.user);
  return isSuperAdmin(user);
}

// --------------- Access / asset type catalogs ---------------

// Any one of a module's codes reads its type catalog — the backend's
// ACCESS_TYPE_READ_CODES / ASSET_TYPE_READ_CODES. [HW-942]
const ACCESS_TYPE_READ_CODES = ['access.types.view', 'access.types.manage', 'access.view', 'access.manage'];
const ASSET_TYPE_READ_CODES = ['assets.types.view', 'assets.types.manage', 'assets.view', 'assets.manage'];

export function useCanReadAccessTypes(): boolean {
  return usePermissions(ACCESS_TYPE_READ_CODES, 'any');
}

export function useCanReadAssetTypes(): boolean {
  return usePermissions(ASSET_TYPE_READ_CODES, 'any');
}

// --------------- Recruitment view gates ---------------

// Recruitment view permission codes — any one is enough to see the corresponding
// module (the backend list endpoints narrow by `.view.assigned` themselves).
const VACANCY_VIEW_CODES = [
  'recruitment.vacancies.view',
  'recruitment.vacancies.view.all',
  'recruitment.vacancies.view.assigned',
] as const;
const CANDIDATE_VIEW_CODES = [
  'recruitment.candidates.view',
  'recruitment.candidates.view.all',
  'recruitment.candidates.view.assigned',
] as const;

export function canViewVacancies(user: AuthenticatedUser | null): boolean {
  return hasPermissions(user, [...VACANCY_VIEW_CODES], 'any');
}

export function canViewCandidates(user: AuthenticatedUser | null): boolean {
  return hasPermissions(user, [...CANDIDATE_VIEW_CODES], 'any');
}

export function canViewRecruitment(user: AuthenticatedUser | null): boolean {
  return canViewVacancies(user) || canViewCandidates(user);
}

export function useCanViewVacancies(): boolean {
  return usePermissions([...VACANCY_VIEW_CODES], 'any');
}

export function useCanViewCandidates(): boolean {
  return usePermissions([...CANDIDATE_VIEW_CODES], 'any');
}

export function useCanViewRecruitment(): boolean {
  const v = useCanViewVacancies();
  const c = useCanViewCandidates();
  return v || c;
}

// --------------- Employee documents ---------------

// Documents are the one surface where the scope hierarchy above must NOT apply.
// `matchPermission` lets `employees.documents.view.self` satisfy a check for the
// base `employees.documents.view` (mirroring the backend guard), which is right
// for reaching the endpoint but wrong for deciding whether someone may see
// ANOTHER employee's documents: `DocumentsService.resolveViewScope` compares the
// codes below verbatim and answers 403 for a self-only holder on a foreign
// profile. So this check mirrors that service — exact codes, no widening. [HW-785]
const DOCUMENTS_VIEW_ALL_CODES = ['employees.documents.view.all', 'employees.documents.view'];
const DOCUMENTS_VIEW_SELF_CODE = 'employees.documents.view.self';

/**
 * Whether the current user may see the Documents section of `employeeId`'s
 * profile. Pass the profile being viewed — the self-scoped grant only counts on
 * the user's own profile, exactly as the backend resolves it.
 */
export function useCanViewEmployeeDocuments(employeeId: string | null | undefined): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => {
    if (!user) return false;
    if (user.role === USER_ROLES.SUPER_ADMIN) return true;
    const granted = user.permissions ?? [];
    if (DOCUMENTS_VIEW_ALL_CODES.some((code) => granted.includes(code))) return true;
    const isSelf = !!employeeId && user.employee?.id === employeeId;
    return isSelf && granted.includes(DOCUMENTS_VIEW_SELF_CODE);
  }, [user, employeeId]);
}

// --------------- Employee notes ---------------

// Same trap as documents: any scoped `employees.notes.view.<scope>` satisfies a
// check for the base `employees.notes.view`, but `EmployeesService.getEmployeeNotes`
// opens every profile only to `employees.notes.view` or `employees.notes.view.all`
// (directly or through a wildcard) and keeps any other scoped holder on their own
// profile, answering 403 elsewhere. This mirrors that service. [HW-1053]
const NOTES_VIEW_CODE = 'employees.notes.view';
const NOTES_VIEW_ALL_CODES = [NOTES_VIEW_CODE, `${NOTES_VIEW_CODE}.all`];

/** Whether `user` may see the Notes of `employeeId`'s profile. */
export function canViewEmployeeNotes(
  user: AuthenticatedUser | null,
  employeeId: string | null | undefined
): boolean {
  if (!user) return false;
  if (user.role === USER_ROLES.SUPER_ADMIN) return true;
  const granted = user.permissions ?? [];
  if (granted.some((p) => NOTES_VIEW_ALL_CODES.some((code) => grantsUnscoped(p, code)))) {
    return true;
  }
  const ownId = user.employee?.id;
  const isSelf = !!employeeId && !!ownId && ownId.toLowerCase() === employeeId.toLowerCase();
  return isSelf && granted.some((p) => matchPermission(p, NOTES_VIEW_CODE));
}

export function useCanViewEmployeeNotes(employeeId: string | null | undefined): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => canViewEmployeeNotes(user, employeeId), [user, employeeId]);
}

// --------------- FOP payment details ---------------

const PAYMENT_DETAILS_VIEW = 'employees.payment-details.view';
const PAYMENT_DETAILS_EDIT = 'employees.payment-details.edit';

/**
 * Whether `user` may read `employeeId`'s FOP payment details, as
 * `EmployeePaymentDetailsService.get` decides it: super_admin or
 * `employees.payment-details.view`, or the employee on their own card — but
 * not while an admin is signed in as them, which the backend refuses. The FE
 * asks the same so it never fires a request that can only 403. [HW-1008]
 */
export function canViewPaymentDetails(
  user: AuthenticatedUser | null,
  employeeId: string | null | undefined,
  isImpersonating: boolean
): boolean {
  if (!user || !employeeId) return false;
  if (!isImpersonating && user.employee?.id === employeeId) return true;
  return hasPermission(user, PAYMENT_DETAILS_VIEW);
}

/** PUT and DELETE ask for view and edit together, so the pencil does too. [HW-1008] */
export function canEditPaymentDetails(user: AuthenticatedUser | null): boolean {
  return hasPermissions(user, [PAYMENT_DETAILS_VIEW, PAYMENT_DETAILS_EDIT], 'all');
}

export function useCanViewPaymentDetails(employeeId: string | null | undefined): boolean {
  const user = useAuthStore((s) => s.user);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  return useMemo(
    () => canViewPaymentDetails(user, employeeId, isImpersonating),
    [user, employeeId, isImpersonating]
  );
}

export function useCanEditPaymentDetails(): boolean {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => canEditPaymentDetails(user), [user]);
}

// Backward-compatible hooks (delegate to generic permission checks)
export function useCanModifyEmployee(): boolean {
  return usePermission('employees.profile.delete');
}

export function useCanViewCompensations(): boolean {
  return usePermission('employees.salary.view');
}

export function useCanEditCompensations(): boolean {
  return usePermission('employees.salary.edit');
}
